import { useEffect, useMemo, useState } from "react";
import { ComponentProps } from "../registry";
import { useBound } from "../helpers";
import { useStore } from "../../store";
import { NestedValue } from "../NestedValue";
import { dispatchAction } from "../../action";
import { formatScalar, isScalar, labelizeKey } from "../../format";
import { ActionDescriptor, BoundValue } from "../../types";

/**
 * Column defs arrive as plain data (the catalog's BoundList). Supported keys:
 *   key       — row field to display
 *   label     — header text (defaults to Title Case of key)
 *   sortable  — opt out of sorting for this column
 *   action    — row-action column: either a journey action name (string) or
 *               a full ActionDescriptor; dispatched with the ROW as scope, so
 *               descriptor context paths resolve relative to the row — the
 *               same pattern as a Button inside a templated List.
 *   context   — with a string `action`: { formKey: rowPath } pairs sent as
 *               the action's context (e.g. { "pet_id": "id" }).
 *   primary   — row-action button styled as primary.
 */
interface ColumnDef {
  key?: string;
  label?: string;
  sortable?: boolean;
  action?: string | ActionDescriptor;
  context?: Record<string, string>;
  primary?: boolean;
}

const DERIVE_SCAN_ROWS = 25;
const SKELETON_ROWS = 5;
const SKELETON_COLS = 3;       // placeholder cells when no columns are known yet

/** No columns declared → derive them from the rows' keys, first-seen order. */
function deriveColumns(rows: Record<string, unknown>[]): ColumnDef[] {
  const keys: string[] = [];
  const seen = new Set<string>();
  for (const row of rows.slice(0, DERIVE_SCAN_ROWS)) {
    if (!row || typeof row !== "object" || Array.isArray(row)) continue;
    for (const k of Object.keys(row)) {
      if (!seen.has(k)) { seen.add(k); keys.push(k); }
    }
  }
  return keys.map((k) => ({ key: k }));
}

function descriptorFor(col: ColumnDef): ActionDescriptor | null {
  if (!col.action) return null;
  if (typeof col.action === "string") {
    return {
      name: { literalString: col.action },
      context: Object.entries(col.context ?? {}).map(([k, p]) => ({
        key: { literalString: k },
        value: { path: p } as BoundValue,
      })),
    };
  }
  return col.action;
}

function headerLabel(col: ColumnDef): string {
  if (col.label) return col.label;
  if (col.key) return labelizeKey(col.key);
  return "";
}

function RowActionButton({ col, row }: { col: ColumnDef; row: unknown }) {
  const [pending, setPending] = useState(false);
  const descriptor = descriptorFor(col);
  if (!descriptor) return null;
  const label = col.label
    ?? (typeof col.action === "string" ? labelizeKey(col.action) : "Open");
  return (
    <button
      type="button"
      className={`a2-button a2-button-sm${col.primary ? " primary" : ""}`}
      disabled={pending}
      onClick={async () => {
        if (pending) return;
        setPending(true);
        try { await dispatchAction(descriptor, row); }
        finally { setPending(false); }
      }}
    >
      {label}
    </button>
  );
}

/** One table cell — scalar truncation with tooltip, NestedValue for the rest. */
function Cell({ value }: { value: unknown }) {
  if (isScalar(value)) {
    const s = formatScalar(value);
    return (
      <span className="a2-cell-trunc" title={s.length > 32 ? s : undefined}>
        {s}
      </span>
    );
  }
  return <NestedValue value={value} depth={1} />;
}

export function DataTable({ fields, scope }: ComponentProps) {
  const rows = useBound<Record<string, unknown>[]>(fields.rows as BoundValue, scope) ?? [];
  const declared = useBound<ColumnDef[]>(fields.columns as BoundValue, scope) ?? [];
  const columns = declared.length > 0 ? declared : deriveColumns(rows);
  const rawPageSize = useBound<number>(fields.pageSize as BoundValue, scope);
  const pageSize = rawPageSize && rawPageSize > 0 ? rawPageSize : Math.max(rows.length, 1);
  const sortable = useBound<boolean>(fields.sortable as BoundValue, scope) ?? false;
  const pendingData = useStore((s) => s.pendingData);

  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [page, setPage] = useState(0);

  // Reset transient view state when the bound row set changes (refresh / filter
  // / a different entity loading into the same DataTable instance).
  useEffect(() => { setPage(0); }, [rows]);
  useEffect(() => {
    if (sortKey && !columns.some((c) => c.key === sortKey)) setSortKey(null);
  }, [columns, sortKey]);

  const view = useMemo(() => {
    if (!sortKey) return rows;
    const dir = sortDir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return -1;
      if (bv == null) return 1;
      const an = Number(av), bn = Number(bv);   // numeric when both coerce, else text
      if (Number.isFinite(an) && Number.isFinite(bn)) return (an - bn) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [rows, sortKey, sortDir]);
  const totalPages = Math.max(1, Math.ceil(view.length / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const pageRows = view.slice(safePage * pageSize, (safePage + 1) * pageSize);

  return (
    <div className="a2-datatable">
      <div className="a2-datatable-scroll">
        <table>
          <thead>
            <tr>
              {columns.map((c, ci) => {
                const canSort = sortable && c.sortable !== false && !!c.key && !c.action;
                return (
                  <th
                    key={c.key ?? `col${ci}`}
                    className={`${canSort ? "sortable" : ""}${c.action ? " action-col" : ""}`}
                    onClick={() => {
                      if (!canSort || !c.key) return;
                      if (sortKey === c.key) setSortDir(sortDir === "asc" ? "desc" : "asc");
                      else { setSortKey(c.key); setSortDir("asc"); }
                    }}
                  >
                    {headerLabel(c)}
                    {c.key && sortKey === c.key && (
                      <span className="a2-sort-arrow">{sortDir === "asc" ? "▲" : "▼"}</span>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              pendingData ? (
                Array.from({ length: SKELETON_ROWS }).map((_, ri) => (
                  <tr key={`sk${ri}`} className="a2-skeleton-row">
                    {Array.from({ length: columns.length || SKELETON_COLS }).map((_c, ci) => (
                      <td key={ci}><span className="a2-skeleton-bar" /></td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={Math.max(columns.length, 1)} className="a2-datatable-empty">
                    No rows
                  </td>
                </tr>
              )
            ) : pageRows.map((r, i) => (
              <tr key={String(r["id"] ?? r["_id"] ?? i)}>
                {columns.map((c, ci) => (
                  <td key={c.key ?? `col${ci}`} className={c.action ? "action-col" : undefined}>
                    {c.action
                      ? <RowActionButton col={c} row={r} />
                      : <Cell value={c.key ? r[c.key] : null} />}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="a2-datatable-pager">
          <button onClick={() => setPage(Math.max(0, safePage - 1))} disabled={safePage === 0}>‹</button>
          <button
            onClick={() => setPage(Math.min(totalPages - 1, safePage + 1))}
            disabled={safePage >= totalPages - 1}
          >›</button>
          <span>Page {safePage + 1} of {totalPages}</span>
        </div>
      )}
    </div>
  );
}
